/**
 * The WWFee-Component displays all members in a table and displays options for filtering and sorting the members
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  Typography,
  useTheme,
  Box,
  styled,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
} from "@mui/material";
import { UnfoldMore, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import useFinance from "../../hooks/finance/useFinance";
import DirectDebitNotificationReportDialog from "../../components/finance/DirectDebitNotificationReportDialog";
import {
  buildFeeCsvExportData,
  buildFeeCsvExportDataForReportMembers,
  getFilteredAndSortedMembers,
  getMinDirectDebitExecutionDate,
  validateDirectDebitExecutionDate,
} from "../../utils/financeUtils";
import { downloadCsvFile } from "../../utils/csvUtils";
import { showErrorMessage } from "../../utils/toastUtils";
import { DirectDebitNotificationReportDto } from "../../types/membersTypes";

// styled form component
const StyledForm = styled("form")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
    alignItems: "flex-end",
  },
  [theme.breakpoints.down("xl")]: {
    display: "flex",
    alignItems: "center",
  },
}));

/**
 * Depicts a table with all members and a filter section to filter the members
 */
const WWFee: React.FunctionComponent = () => {
  const theme = useTheme();

  /**
   * Function which provides the styles of the WWFee
   */
  const styles = {
    amountOfEntries: {
      marginBottom: "10px",
      padding: "7px",
      textAlign: "center",
      fontWeight: "bold",
    },
    filterContainer: {
      marginBottom: "10px",
    },
    filterElement: {
      [theme.breakpoints.up("md")]: {
        margin: "7px",
        width: "155px",
      },
      [theme.breakpoints.down("xl")]: {
        margin: "7px",
        width: "120px",
      },
      [theme.breakpoints.down("lg")]: {
        margin: "7px",
        width: "120px",
      },
    },
    statusFilter: {
      [theme.breakpoints.up("md")]: {
        margin: "7px",
        width: "165px",
      },
      [theme.breakpoints.down("xl")]: {
        margin: "7px",
        width: "100px",
      },
    },
    filterBtn: {
      [theme.breakpoints.up("md")]: {
        marginTop: "12px",
        marginBottom: "7px",
        marginRight: "5px",
        marginLeft: "50px",
      },
      [theme.breakpoints.down("xl")]: {
        marginTop: "15px",
        marginBottom: "7px",
        marginRight: "5px",
        marginLeft: "25px",
      },
    },
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
    sortElement: {
      margin: "7px",
      width: "205px",
    },
    tableContainer: {
      maxHeight: (window.screen.height - 75) * 0.8,
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    tableHeadSortBtn: {
      display: "flex",
      alignItems: "center",
    },
    statusFilterMain: {
      display: "block",
      "@media screen and (max-width: 350px)": {
        display: "none",
      },
    },
    statusFilterAdditional: {
      display: "none",
      "@media screen and (max-width: 350px)": {
        display: "block",
      },
    },
    accountFilterMain: {
      "@media screen and (orientation:landscape)": {
        display: "block",
      },
      "@media screen and (orientation:portrait)": {
        display: "none",
      },
    },
    accountFilterAdditional: {
      "@media screen and (orientation:landscape)": {
        display: "none",
      },
      "@media screen and (orientation:portrait)": {
        display: "block",
      },
    },
  };

  const StyledLink = styled(Link)`
    text-decoration: none;
    color: black;
    &:hover {
      text-decoration: underline;
    }
  `;

  interface SelectedAmount {
    memberId: number;
    amount: string;
    usesCustomAmount: boolean;
  }

  const [additionalFiltersState, setAddtionalFiltersState] = useState(false);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sortOption, setSortOption] = useState<string>("");
  const [nameSort, setNameSort] = useState<string>("");
  const [radioState, setRadioState] = useState<boolean>(false);
  const [paymentReason, setPaymentReason] = useState<string>(`JBT-Working-Weekend`);
  const [bookingId, setBookingId] = useState<string>(`JBT-WW`);
  // Default the booking date to 19 days in the future, which is a good default considering the 14-day notification period plus processing time and buffer
  const bookingDateDefault = useMemo(() => dayjs().add(19, "day"), []);
  const [bookingDate, setBookingDate] = useState<Dayjs>(bookingDateDefault);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [selectedAmounts, setSelectedAmounts] = useState<SelectedAmount[]>([]);

  const { memberWWFeeInformation, lastWW, sendDirectDebitNotificationsForMembers, isSendingDirectDebitNotifications } =
    useFinance();

  const [openDialog, setOpenDialog] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [notificationReport, setNotificationReport] = useState<DirectDebitNotificationReportDto | null>(null);
  const [failedCsvExportData, setFailedCsvExportData] = useState<object[]>([]);

  const getMemberSelection = (memberId: number): SelectedAmount | undefined => {
    return selectedAmounts.find((entry) => entry.memberId === memberId);
  };

  /**
   * Used to resolve the correct amount for a member based on the selection
   * when building the CSV export data or sending the direct debit notifications.
   */
  const resolveAmount = (selection: SelectedAmount | undefined): string => {
    if (!selection) {
      return "";
    }
    return selection.usesCustomAmount ? customAmount : selection.amount;
  };

  const filteredAndSortedMembers = useMemo(() => {
    return getFilteredAndSortedMembers({
      memberFinanceInformation: memberWWFeeInformation,
      statusFilter,
      searchFilter,
      sortOption,
      nameSort,
    });
  }, [memberWWFeeInformation, statusFilter, searchFilter, sortOption, nameSort]);

  const csvExportData = useMemo(() => {
    const membersWithAmount = filteredAndSortedMembers.filter((member) => {
      const selectedAmount = resolveAmount(getMemberSelection(member.memberId));
      return selectedAmount !== "" && selectedAmount !== "00,00";
    });
    return buildFeeCsvExportData(membersWithAmount, paymentReason, (member) => {
      return resolveAmount(getMemberSelection(member.memberId));
    });
  }, [filteredAndSortedMembers, selectedAmounts, paymentReason, customAmount]);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleReportDialogClose = () => {
    setReportDialogOpen(false);
    setNotificationReport(null);
    setFailedCsvExportData([]);
  };

  const getAmountByMemberId = (memberId: number): string => {
    return resolveAmount(getMemberSelection(memberId));
  };

  /**
   * Confirms SEPA creation: downloads the CSV for members with a non-zero amount, optionally sends notification emails, and closes the dialog.
   */
  const handleDialogConfirm = async () => {
    if (bookingId.trim() === "") {
      showErrorMessage("Bitte gib eine Abbuchungs ID ein");
      return;
    }
    if (paymentReason.trim() === "") {
      showErrorMessage("Bitte gib einen Verwendungszweck ein");
      return;
    }
    const executionDateError = validateDirectDebitExecutionDate(bookingDate);
    if (executionDateError !== null) {
      showErrorMessage(executionDateError);
      return;
    }

    const recipientMembers = filteredAndSortedMembers.filter((member) => {
      const selectedAmount = resolveAmount(getMemberSelection(member.memberId));
      return selectedAmount !== "" && selectedAmount !== "00,00";
    });
    if (recipientMembers.length === 0) {
      showErrorMessage("Bitte wähle mindestens ein Mitglied aus");
      return;
    }
    const recipients = recipientMembers.map((member) => ({
      memberId: member.memberId,
      amount: resolveAmount(getMemberSelection(member.memberId)),
    }));
    const sendEmails = !radioState;

    try {
      const report = await sendDirectDebitNotificationsForMembers({
        recipients,
        executionDate: bookingDate.toISOString(),
        eventName: paymentReason,
        sendEmails,
      });
      handleDialogClose();

      if (sendEmails) {
        if (report.notified.length > 0) {
          downloadCsvFile(
            buildFeeCsvExportDataForReportMembers(
              recipientMembers,
              report.notified,
              paymentReason,
              getAmountByMemberId
            ),
            "ww_fee_export",
            { includeHeaders: false }
          );
        }
        if (report.failed.length > 0) {
          setFailedCsvExportData(
            buildFeeCsvExportDataForReportMembers(recipientMembers, report.failed, paymentReason, getAmountByMemberId)
          );
          setNotificationReport(report);
          setReportDialogOpen(true);
        }
      } else {
        downloadCsvFile(csvExportData, "ww_fee_export", { includeHeaders: false });
      }
    } catch {
      showErrorMessage("Benachrichtigungs-E-Mails konnten nicht versendet werden");
    }
  };

  useEffect(() => {
    setPaymentReason(`JBT-Working-Weekend ${lastWW?.eventBeginn ? dayjs(lastWW.eventBeginn).format("MM/YYYY") : ""}`);
    setBookingId(`JBT-WW-${lastWW?.eventBeginn ? dayjs(lastWW.eventBeginn).format("MM-YYYY") : ""}`);
  }, [lastWW]);

  useEffect(() => {
    if (memberWWFeeInformation.length > 0) {
      const initialSelectedAmounts: SelectedAmount[] = memberWWFeeInformation.map((member) => ({
        memberId: member.memberId,
        amount: member.amount,
        usesCustomAmount: false,
      }));
      setSelectedAmounts(initialSelectedAmounts);
    }
  }, [memberWWFeeInformation]);

  /**
   * Handles the changes of the radio buttons for the payment amount
   */
  const setMemberAmount = (memberId: number, amount: string, usesCustomAmount: boolean) => {
    setSelectedAmounts((prev) => {
      const existingEntry = prev.find((entry) => entry.memberId === memberId);

      if (existingEntry) {
        return prev.map((entry) => (entry.memberId === memberId ? { memberId, amount, usesCustomAmount } : entry));
      }

      return [...prev, { memberId, amount, usesCustomAmount }];
    });
  };

  /**
   * Handles the changes of the radio buttons for the emails
   */
  const handleRadioChange: VoidFunction = () => {
    setRadioState(!radioState);
  };

  /**
   * Handles the change event on the payment reason input
   */
  const handlePaymentReason = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPaymentReason(event.target.value);
  };

  /**
   * Handles the change event on the booking ID input
   */
  const handleBookingId = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setBookingId(event.target.value);
  };

  /**
   * Handles the change event on the booking date input
   */
  const handleBookingDate = (date: Dayjs | null): void => {
    if (date !== null) {
      setBookingDate(date);
    } else {
      setBookingDate(bookingDateDefault);
    }
  };

  /**
   * Handles the change event on the amount input
   */
  const handleAmount = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const euroRegex = /^[0-9]*([,][0-9]{0,2})?$/;
    if (value === "" || euroRegex.test(value)) {
      setCustomAmount(value);
    }
  };

  /**
   * Handles the change event on the search filter input
   * @param event
   */
  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilter(event.target.value);
  };

  /**
   * Handles the change event on the status filter input
   * @param event
   */
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setStatusFilter(event.target.value);
  };

  /**
   * Handles the change event on the sort input
   * @param event
   */
  const handleSortOptionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSortOption(event.target.value);
  };

  /**
   * Handles the filter toggle
   */
  const toggleFilters: VoidFunction = () => {
    setAddtionalFiltersState(!additionalFiltersState);
  };

  /**
   * Toggles between the name sort options
   */
  const toggleNameSort: VoidFunction = () => {
    switch (nameSort) {
      case "": {
        setNameSort("up");
        break;
      }
      case "up": {
        setNameSort("down");
        break;
      }
      case "down": {
        setNameSort("");
        break;
      }
    }
  };

  /**
   * Returns the sort icon for the name column
   */
  const getNameSortIcon = (): React.JSX.Element => {
    switch (nameSort) {
      case "up": {
        return <ExpandLess />;
      }
      case "down": {
        return <ExpandMore />;
      }
      default: {
        return <UnfoldMore />;
      }
    }
  };

  // The additional filters
  const additionalFilters = (
    <div>
      <TextField
        sx={styles.sortElement}
        color="primary"
        onChange={handleSortOptionChange}
        value={sortOption}
        select
        label="Sortieren nach..."
      >
        <MenuItem value={""}>-</MenuItem>
        <MenuItem value={"lastchange ASC"}>Zuletzt Aktualisiert (aufsteigend)</MenuItem>
        <MenuItem value={"lastchange DESC"}>Zuletzt Aktualisiert (absteigend)</MenuItem>
      </TextField>
    </div>
  );

  const renderTableBody = (): React.ReactNode => {
    return filteredAndSortedMembers.map((member) => {
      const selection = getMemberSelection(member.memberId);

      return (
        <TableRow hover key={member.memberId}>
          <TableCell component="th" scope="row">
            <Typography color="secondary">
              <StyledLink
                to={`/gesamtuebersicht/${member.memberId}`}
              >{`${member.firstName} ${member.lastName}`}</StyledLink>
            </Typography>
          </TableCell>
          <TableCell>{member.memberStatus}</TableCell>
          <TableCell>
            <Radio
              checked={selection?.usesCustomAmount === true}
              onChange={() => setMemberAmount(member.memberId, customAmount, true)}
            />
          </TableCell>
          <TableCell>
            <Radio
              checked={selection?.amount === "00,00" && !selection?.usesCustomAmount}
              onChange={() => setMemberAmount(member.memberId, "00,00", false)}
            />
          </TableCell>
          <TableCell>
            <Radio
              checked={selection?.amount === "20,00" && !selection?.usesCustomAmount}
              onChange={() => setMemberAmount(member.memberId, "20,00", false)}
            />
          </TableCell>
          <TableCell>
            <Radio
              checked={selection?.amount === "15,00" && !selection?.usesCustomAmount}
              onChange={() => setMemberAmount(member.memberId, "15,00", false)}
            />
          </TableCell>
          <TableCell>
            <Radio
              checked={selection?.amount === "40,00" && !selection?.usesCustomAmount}
              onChange={() => setMemberAmount(member.memberId, "40,00", false)}
            />
          </TableCell>
          <TableCell>
            <Radio
              checked={selection?.amount === "30,00" && !selection?.usesCustomAmount}
              onChange={() => setMemberAmount(member.memberId, "30,00", false)}
            />
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div>
      <Grid container spacing={4} sx={{ paddingBottom: "20px" }}>
        <Grid item>
          <Button sx={styles.button} variant="contained" component={Link} to={`/finanzuebersicht/`}>
            Zurück
          </Button>
        </Grid>
        <Grid item>
          <Button
            sx={styles.button}
            variant="contained"
            onClick={() => {
              setOpenDialog(true);
            }}
          >
            SEPA-CSV erstellen
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ paddingBottom: "10px" }}>
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ paddingRight: "200px" }}>
            <strong>Wichtige Information:</strong> Um einen Beitrag per SEPA-Lastschrift einzuziehen, müssen die
            betroffenen Mitglieder 2 Wochen vorab informiert werden. Dazu wird beim Generieren der SEPA-CSV-Datei eine{" "}
            <strong>E-Mail verschickt</strong>, die jeden JBT-ler informiert, der etwas bezahlen muss. In der E-Mail
            enthalten sind der zu zahlende Betrag, die IBAN, von der abgebucht wird (zur Kontrolle), sowie ein Hinweis
            darauf, dass innerhalb von 14 Tagen <strong>widersprochen</strong> werden kann. Deswegen muss mit dem
            weiteren Verwenden der CSV-Datei 14 Tage <strong>gewartet</strong> werden! Erst dann kann die Lastschrift
            veranlasst werden (z.B. in StarMoney), wobei darauf geachtet werden muss, dass die Bank weitere 2 Tage vor
            der eigentlichen Durchführung der Lastschrift die Datei bekommen muss. Dementsprechend weit in der Zukunft
            (14+2 Tage plus Puffer plus Wochenende) sollte das Ausführungsdatum liegen.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item container alignItems="center" spacing={2} xs={12}>
              <Grid item sx={{ width: "200px" }}>
                <Typography variant="body1">Verwendungszweck:</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="outlined"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "36px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 14px",
                    },
                  }}
                  value={paymentReason}
                  onChange={handlePaymentReason}
                />
              </Grid>
            </Grid>

            <Grid item container alignItems="center" spacing={2} xs={12}>
              <Grid item sx={{ width: "200px" }}>
                <Typography variant="body1">ID der Abbuchung:</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="outlined"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "36px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 14px",
                    },
                  }}
                  value={bookingId}
                  onChange={handleBookingId}
                />
              </Grid>
            </Grid>

            <Grid item container alignItems="center" spacing={2} xs={12}>
              <Grid item sx={{ width: "200px" }}>
                <Typography variant="body1">Ausführungsdatum:</Typography>
              </Grid>
              <Grid item xs>
                <DatePicker
                  value={bookingDate}
                  onChange={handleBookingDate}
                  minDate={getMinDirectDebitExecutionDate()}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-root": {
                      height: "36px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 14px",
                    },
                  }}
                  slotProps={{ textField: { variant: "outlined" } }}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center" spacing={2} xs={12}>
              <Grid item sx={{ width: "200px" }}>
                <Typography variant="body1">Variabler Betrag:</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="outlined"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "36px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 14px",
                    },
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "[0-9]*[,]{0,1}[0-9]{0,2}",
                  }}
                  value={customAmount}
                  onChange={handleAmount}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center" spacing={2} xs={12}>
              <Grid item sx={{ width: "200px" }}>
                <Typography variant="body1">Mails versenden:</Typography>
              </Grid>
              <Grid item xs>
                <RadioGroup row defaultValue="ja">
                  <FormControlLabel value="ja" control={<Radio />} label="Ja" onChange={() => handleRadioChange()} />
                  <FormControlLabel
                    value="nein"
                    control={<Radio />}
                    label="Nein"
                    onChange={() => handleRadioChange()}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Paper sx={styles.filterContainer}>
        <StyledForm noValidate autoComplete="off">
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}>
              <TextField label="Name" sx={styles.filterElement} color="primary" onChange={handleSearchInput} />
            </Grid>
            <Grid item xs={6} sm={3} sx={styles.statusFilterMain}>
              <TextField
                label="Status"
                sx={styles.filterElement}
                color="primary"
                onChange={handleStatusChange}
                value={statusFilter}
                select
              >
                <MenuItem value={""}>-</MenuItem>
                <MenuItem value={"Trainee"}>Trainee</MenuItem>
                <MenuItem value={"aktives Mitglied"}>aktives Mitglied</MenuItem>
                <MenuItem value={"Senior"}>Senior</MenuItem>
                <MenuItem value={"passives Mitglied"}>passives Mitglied</MenuItem>
                <MenuItem value={"Alumnus"}>Alumnus</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <IconButton aria-label="more filter options" sx={styles.filterBtn} onClick={toggleFilters} size="large">
            {additionalFiltersState ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
          </IconButton>
        </StyledForm>
        {additionalFiltersState ? additionalFilters : null}
        <Box sx={styles.amountOfEntries}>{`${filteredAndSortedMembers.length} Einträge`}</Box>
      </Paper>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>
                <Box sx={styles.tableHeadSortBtn} onClick={toggleNameSort}>
                  <>
                    Name
                    {getNameSortIcon()}
                  </>
                </Box>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>Status</TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <div>
                  <span style={{ display: "block" }}>Variabler</span>
                  <span style={{ display: "block" }}>Betrag</span>
                </div>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <div>
                  <span style={{ display: "block" }}>Nichts</span>
                  <span style={{ display: "block" }}>abbuchen</span>
                </div>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>20,00 €</TableCell>
              <TableCell sx={styles.tableHeadCell}>15,00 €</TableCell>
              <TableCell sx={styles.tableHeadCell}>40,00 €</TableCell>
              <TableCell sx={styles.tableHeadCell}>30,00 €</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={() => handleDialogClose()}>
        <DialogTitle>Bestätigung</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {radioState
              ? "WARNUNG: Es werden KEINE E-Mails an die ausgewählten Mitglieder gesendet. Dies muss noch gemacht werden, um das Gesetz einzuhalten"
              : "WARNUNG: Es werden E-Mails an alle ausgewählten Mitglieder gesendet"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose()} color="primary" disabled={isSendingDirectDebitNotifications}>
            Abbrechen
          </Button>
          <Button onClick={() => handleDialogConfirm()} color="primary" disabled={isSendingDirectDebitNotifications}>
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
      <DirectDebitNotificationReportDialog
        open={reportDialogOpen}
        onClose={handleReportDialogClose}
        report={notificationReport}
        failedCsvExportData={failedCsvExportData}
        failedCsvFileName="ww_fee_export_nicht_benachrichtigt"
      />
    </div>
  );
};

export default WWFee;
